import React, { useContext, useState } from 'react';
import { Alert, Button, Modal } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import SurveyContext from '@totem/components/surveyV2/instanceDetail/SurveyInstanceContext';
import { validateSurvey } from '@totem/components/surveyV2/utilities/SurveyUtilities';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
export type Props = {
  disabled: boolean;
};

const SubmitButton = ({ disabled }: Props) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { userProfile } = useContext(UserProfileContext);
  const { data, loading, setErrors, errors, submit } =
    useContext(SurveyContext);
  const { survey } = data;
  const { isSubmitted } = survey;
  const isAdmin =
    isNotNull(userProfile.organization) &&
    permissions.organizationalAdministrator.hasPermissions(userProfile);
  const reopenAvailable = isAdmin && isSubmitted;

  const getMessage = (): string => {
    return isSubmitted
      ? 'Are you sure you want to reopen this survey?'
      : 'Are you sure you want to submit this survey? Once submitted, answers cannot be changed. This action cannot be undone.';
  };

  const toggleModal = () => {
    const newErrors = validateSurvey(survey);

    setErrors(newErrors);

    setIsVisible(!isVisible);
  };

  const handleSubmit = () => {
    submit(data.survey.id, 'submit');

    setIsVisible(false);
  };

  const handleReopen = () => {
    submit(data.survey.id, 'reopen');

    setIsVisible(false);
  };

  return (
    <>
      <IBTooltip content={permissions.organizationalAdministrator.tooltip}>
        <Button onClick={toggleModal} disabled={isSubmitted && !isAdmin}>
          {reopenAvailable ? 'Reopen Survey' : 'Submit Survey'}
        </Button>
      </IBTooltip>
      <Modal
        open={isVisible}
        confirmLoading={loading || disabled}
        onOk={isSubmitted ? handleReopen : handleSubmit}
        onCancel={toggleModal}
        title="Confirm"
        okText={isSubmitted ? 'Reopen' : 'Submit'}
        okButtonProps={{ disabled: errors.length > 0 }}
        width={errors.length > 0 ? 'fit-content' : 520}
        style={errors.length > 0 ? { minWidth: 520, maxWidth: '90vw' } : {}}
      >
        {!isSubmitted && errors.length === 0 && getMessage()}
        {isSubmitted && getMessage()}
        {!isSubmitted && errors.length > 0 && (
          <Alert
            title="Error: The following issues must be resolved before submitting."
            description={
              <>
                <table
                  style={{
                    borderSpacing: '16px 0',
                    borderCollapse: 'separate',
                  }}
                >
                  <thead>
                    <tr style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                      <th>Category</th>
                      <th>Question</th>
                      <th>Problem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((item, idx) => (
                      <tr
                        key={idx}
                        style={{ whiteSpace: 'nowrap', textAlign: 'left' }}
                      >
                        <td>{item.categoryName}</td>
                        <td>{item.questionLabel}</td>
                        <td>{item.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            }
            type="error"
            showIcon
          />
        )}
      </Modal>
    </>
  );
};
export default SubmitButton;
